import type { JSX } from "react";

import { Icon, type IconName } from "@frontend/components/atoms/Icon";
import { Toggle } from "@frontend/components/atoms/Toggle";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@frontend/components/atoms/Tooltip";
import { useLingui } from "@lingui/react/macro";
import { useEditorState } from "@tiptap/react";

import { useTextEditorContext } from "./TextEditor.context";
import type {
  TextEditorCommandTool,
  TextEditorToolbarButtonProps,
} from "./TextEditor.types";
import { canRunTool, isToolActive, runTool } from "./tools";

const TOOL_ICONS = {
  bold: "Bold",
  italic: "Italic",
  underline: "Underline",
  strike: "Strikethrough",
  bulletList: "List",
  orderedList: "ListOrdered",
  undo: "Undo2",
  redo: "Redo2",
} as const satisfies Record<TextEditorCommandTool, IconName>;

export function TextEditorToolbarButton({
  tool,
}: TextEditorToolbarButtonProps): JSX.Element {
  const { t } = useLingui();

  const { editor, size, disabled } = useTextEditorContext();

  const labels: Record<TextEditorCommandTool, string> = {
    bold: t`Bold`,
    italic: t`Italic`,
    underline: t`Underline`,
    strike: t`Strikethrough`,
    bulletList: t`Bullet list`,
    orderedList: t`Numbered list`,
    undo: t`Undo`,
    redo: t`Redo`,
  };

  const state = useEditorState({
    editor,
    selector: ({ editor }) => ({
      active: isToolActive(editor, tool),
      enabled: canRunTool(editor, tool),
    }),
  });

  const label = labels[tool];

  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Toggle
            size={size}
            pressed={state?.active ?? false}
            disabled={disabled || false === (state?.enabled ?? false)}
            aria-label={label}
            onMouseDown={(event) => event.preventDefault()}
            onPressedChange={() => {
              if (editor !== null) {
                runTool(editor, tool);
              }
            }}
          >
            <Icon name={TOOL_ICONS[tool]} size={size} />
          </Toggle>
        }
      />

      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}
